(() => {
  'use strict';

  const COMPONENT_NAME = 'one-lux-gold-market';
  const QUOTE_CONTRACT_VERSION = '1';
  const CURRENT_REFERENCE_CACHE_KEY = 'oneLuxuryProductGoldReferenceV1';
  const CURRENT_REFERENCE_MAX_RESPONSE_BYTES = 32 * 1024;
  const CURRENT_REFERENCE_MAX_STALE_MS = 2 * 60 * 60 * 1000;
  const CURRENT_REFERENCE_REFRESH_MS = 10 * 60 * 1000;
  const CURRENT_REFERENCE_TIMEOUT_MS = 10000;
  const CURRENT_REFERENCE_URL = 'https://api.gold-api.com/price/XAU/USD';
  const MAX_FUTURE_TIMESTAMP_MS = 5 * 60 * 1000;
  const MAX_PUBLIC_HISTORY_POINTS = 60;
  const MIN_RETRY_MS = 5000;
  const MAX_RETRY_MS = 120000;
  const CHANGE_BASIS_LABELS = {
    provider_previous_close: 'vs provider previous close',
    provider_session_open: 'vs provider session open',
  };

  if (!window.customElements || window.customElements.get(COMPONENT_NAME)) {
    return;
  }

  const isFiniteNumber = (value) => typeof value === 'number' && Number.isFinite(value);
  const asFiniteNumber = (value) => (isFiniteNumber(value) ? value : null);
  const asString = (value) => (typeof value === 'string' ? value.trim() : '');
  const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);
  const isSameVariant = (responseId, requestedId) => {
    return responseId === requestedId || responseId === `gid://shopify/ProductVariant/${requestedId}`;
  };

  class OneLuxGoldMarket extends HTMLElement {
    constructor() {
      super();
      this.abortController = null;
      this.ageInterval = null;
      this.currentVariant = null;
      this.currentReferenceQuote = null;
      this.currentReferenceTimeout = null;
      this.failureCount = 0;
      this.hostedEmbedTimer = null;
      this.initialized = false;
      this.lastValid = null;
      this.latestQuoteTimestamp = 0;
      this.pollTimer = null;
      this.requestSequence = 0;
      this.resumeScheduled = false;
      this.variants = [];
      this.variantsById = new Map();

      this.handlePickerControlChange = this.handlePickerControlChange.bind(this);
      this.handleOffline = this.handleOffline.bind(this);
      this.handleOnline = this.handleOnline.bind(this);
      this.handlePageHide = this.handlePageHide.bind(this);
      this.handlePageShow = this.handlePageShow.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);
      this.handleVisibilityChange = this.handleVisibilityChange.bind(this);
      this.handleTracking = this.handleTracking.bind(this);
      this.handleHostedIframeLoad = this.handleHostedIframeLoad.bind(this);
    }

    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.cacheElements();
      this.parseVariants();
      this.configure();
      this.currentVariant = this.getVariant(this.dataset.variantId);
      this.bind();
      if (this.isHostedEmbedMode()) {
        this.initializeHostedEmbed();
        return;
      }

      if (this.isCurrentReferenceMode()) {
        this.initializeCurrentReference();
        return;
      }

      this.renderPending();
      this.startAgeTimer();

      if (this.isPrivateProxyMode() && this.isOnline() && !document.hidden) {
        this.refresh();
      }
    }

    disconnectedCallback() {
      this.unbind();
      this.clearPollTimer();
      this.abortRequest();
      this.clearCurrentReferenceTimeout();
      this.clearHostedEmbedTimer();
      if (this.ageInterval) window.clearInterval(this.ageInterval);
      this.ageInterval = null;
      this.initialized = false;
    }

    cacheElements() {
      this.nodes = {
        checkoutComparison: this.querySelector('[data-product-checkout-comparison]'),
        goldChange: this.querySelector('[data-market-gold-change]'),
        goldPrice: this.querySelector('[data-market-gold-price]'),
        marketArea: this.querySelector('[data-market-chart-area]'),
        marketChart: this.querySelector('[data-market-chart]'),
        marketLine: this.querySelector('[data-market-chart-line]'),
        productArea: this.querySelector('[data-product-chart-area]'),
        productCard: this.querySelector('.one-lux-market__product-card'),
        productChange: this.querySelector('[data-product-change]'),
        productChart: this.querySelector('[data-product-chart]'),
        productChartStage: this.querySelector('[data-product-chart-stage]'),
        productLine: this.querySelector('[data-product-chart-line]'),
        productPrimaryLabel: this.querySelector('[data-product-primary-label]'),
        productPrimaryPrice: this.querySelector('[data-product-primary-price]'),
        productTitle: this.querySelector('[data-product-title]'),
        productVariant: this.querySelector('[data-product-variant]'),
        statusText: this.querySelector('[data-market-status-text]'),
        ticker: this.querySelector('[data-product-ticker]'),
        tickerClone: this.querySelector('[data-product-ticker-clone]'),
        tickerWrap: this.querySelector('[data-product-ticker-wrap]'),
        trackButton: this.querySelector('[data-market-track]'),
        trackLabel: this.querySelector('[data-market-track-label]'),
        trackMessage: this.querySelector('[data-market-track-message]'),
        attribution: this.querySelector('[data-market-attribution]'),
        hostedIframe: this.querySelector('[data-market-hosted-iframe]'),
        hostedLoading: this.querySelector('[data-market-hosted-loading]'),
        hostedUnavailable: this.querySelector('[data-market-hosted-unavailable]'),
        updated: this.querySelector('[data-market-updated-text]') || this.querySelector('[data-market-updated]'),
      };
    }

    parseVariants() {
      const source = this.querySelector('[data-one-lux-market-variants]');
      if (!source) return;

      try {
        const variants = JSON.parse(source.textContent || '[]');
        this.variants = variants.filter((variant) => {
          return variant && variant.id != null && isFiniteNumber(asFiniteNumber(variant.checkoutPriceCents));
        }).map((variant) => ({
          id: String(variant.id),
          title: asString(variant.title) || 'Selected option',
          checkoutPriceCents: Number(variant.checkoutPriceCents),
        }));
        this.variants.forEach((variant) => this.variantsById.set(variant.id, variant));
      } catch (error) {
        this.variants = [];
        this.variantsById.clear();
      }
    }

    configure() {
      this.endpoint = asString(this.dataset.endpoint);
      this.sourceMode = ['current_reference', 'hosted_embed', 'private_proxy'].includes(asString(this.dataset.sourceMode))
        ? asString(this.dataset.sourceMode)
        : 'pending';
      this.range = ['1h', '1d', '7d', '30d'].includes(this.dataset.range) ? this.dataset.range : '1d';
      this.refreshMilliseconds = Math.max(10000, Number(this.dataset.refreshSeconds || 10) * 1000 || 10000);
      this.staleThresholdMilliseconds = Math.max(30000, Number(this.dataset.staleThresholdSeconds || 90) * 1000 || 90000);
      this.currency = asString(this.dataset.currency) || 'USD';
      this.productTitle = asString(this.dataset.productTitle);
      this.showCheckoutComparison = this.dataset.showCheckoutComparison !== 'false';
      this.showProductSparkline = this.dataset.showProductSparkline !== 'false';
      this.showProductTicker = this.dataset.showProductTicker !== 'false';
      this.trackingEnabled = this.dataset.trackingEnabled === 'true' && asString(this.dataset.trackingEndpoint) !== '';
      this.trackingEndpoint = asString(this.dataset.trackingEndpoint);
      this.loginUrl = asString(this.dataset.loginUrl) || '/account/login';
      this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    bind() {
      // Some theme picker implementations emit their canonical variant event
      // only after replacing the product section. Listen in capture phase so
      // the existing component can immediately reflect a valid native choice,
      // then let the canonical event remain the source of truth.
      document.addEventListener('change', this.handlePickerControlChange, true);
      document.addEventListener('variant:changed', this.handleVariantChanged);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      window.addEventListener('offline', this.handleOffline);
      window.addEventListener('online', this.handleOnline);
      window.addEventListener('pagehide', this.handlePageHide);
      window.addEventListener('pageshow', this.handlePageShow);

      if (this.nodes.trackButton && this.trackingEnabled) {
        this.nodes.trackButton.addEventListener('click', this.handleTracking);
      }

      if (this.nodes.hostedIframe && this.isHostedEmbedMode()) {
        this.nodes.hostedIframe.addEventListener('load', this.handleHostedIframeLoad);
      }
    }

    unbind() {
      document.removeEventListener('change', this.handlePickerControlChange, true);
      document.removeEventListener('variant:changed', this.handleVariantChanged);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      window.removeEventListener('offline', this.handleOffline);
      window.removeEventListener('online', this.handleOnline);
      window.removeEventListener('pagehide', this.handlePageHide);
      window.removeEventListener('pageshow', this.handlePageShow);

      if (this.nodes && this.nodes.trackButton) {
        this.nodes.trackButton.removeEventListener('click', this.handleTracking);
      }

      if (this.nodes && this.nodes.hostedIframe) {
        this.nodes.hostedIframe.removeEventListener('load', this.handleHostedIframeLoad);
      }
    }

    getVariant(variantId) {
      return this.variantsById.get(String(variantId)) || null;
    }

    handlePickerControlChange(event) {
      const control = event && event.target;
      if (!control || typeof control.closest !== 'function') return;

      const picker = control.closest('variant-picker');
      const productInfo = this.closest('product-info');
      if (!picker || !productInfo || !productInfo.contains(picker)) return;

      const selected = this.getVariantFromPicker(picker);
      if (selected) this.selectVariant(selected);
    }

    getVariantFromPicker(picker) {
      if (!picker) return null;

      const selectedOptions = Array.from(picker.querySelectorAll('.m-product-option')).map((option) => {
        const control = option.querySelector('input[type="radio"]:checked, select');
        return asString(control && control.value);
      });

      if (!selectedOptions.length || selectedOptions.some((option) => !option)) return null;

      return this.variants.find((variant) => {
        const options = variant.title.split(' / ');
        return options.length === selectedOptions.length
          && options.every((option, index) => option === selectedOptions[index]);
      }) || null;
    }

    handleVariantChanged(event) {
      const variant = event && event.detail ? event.detail.variant : null;
      if (!variant || variant.id == null) {
        this.clearSelectedVariant();
        return;
      }

      // Ignore a variant event from another product surface (for example, a quick-buy
      // card). This component only owns variants embedded for its current product.
      const selected = this.getVariant(variant.id);
      if (selected) this.selectVariant(selected);
    }

    selectVariant(variant) {
      if (!variant || (this.currentVariant && this.currentVariant.id === variant.id)) return;

      this.currentVariant = variant;
      this.dataset.variantId = variant.id;
      if (this.isCurrentReferenceMode()) {
        this.renderShopifyVariant();
        return;
      }
      this.lastValid = null;
      this.failureCount = 0;
      this.abortRequest();
      this.clearPollTimer();
      this.renderShopifyVariant();
      if (this.isHostedEmbedMode()) return;
      this.renderPending();

      if (this.isPrivateProxyMode() && !document.hidden) {
        this.refresh();
      }
    }

    clearSelectedVariant() {
      if (!this.currentVariant) return;

      this.currentVariant = null;
      this.dataset.variantId = '';
      if (this.isCurrentReferenceMode()) {
        this.renderShopifyVariant();
        return;
      }
      this.lastValid = null;
      this.failureCount = 0;
      this.abortRequest();
      this.clearPollTimer();
      if (this.isHostedEmbedMode()) {
        this.renderShopifyVariant();
        return;
      }
      this.renderPending();
    }

    handleVisibilityChange() {
      if (document.hidden) {
        this.clearPollTimer();
        this.requestSequence += 1;
        this.abortRequest();
        return;
      }

      this.requestResume();
    }

    handleOffline() {
      this.clearPollTimer();
      this.requestSequence += 1;
      this.abortRequest();
      if (this.isCurrentReferenceMode()) {
        this.renderCurrentReferenceFailure();
        return;
      }
      if (this.isPrivateProxyMode()) this.renderError('OFFLINE');
    }

    handleOnline() {
      this.requestResume();
    }

    handlePageHide() {
      this.clearPollTimer();
      this.requestSequence += 1;
      this.abortRequest();
    }

    handlePageShow() {
      this.requestResume();
    }

    requestResume() {
      if ((!this.isPrivateProxyMode() && !this.isCurrentReferenceMode()) || document.hidden || !this.isOnline() || this.resumeScheduled || this.abortController) return;
      this.resumeScheduled = true;
      Promise.resolve().then(() => {
        this.resumeScheduled = false;
        if (!this.isConnected || document.hidden || !this.isOnline()) return;
        if (this.isCurrentReferenceMode()) {
          this.resumeCurrentReference();
          return;
        }
        if (this.isPrivateProxyMode()) this.refresh();
      });
    }

    isOnline() {
      return typeof navigator === 'undefined' || navigator.onLine !== false;
    }

    isHostedEmbedMode() {
      return this.sourceMode === 'hosted_embed';
    }

    isCurrentReferenceMode() {
      return this.sourceMode === 'current_reference';
    }

    isPrivateProxyMode() {
      return this.sourceMode === 'private_proxy' && this.endpoint !== '';
    }

    initializeHostedEmbed() {
      this.setState('hosted');
      this.dataset.tone = 'neutral';
      this.renderShopifyVariant();
      if (!this.nodes.hostedIframe) return;

      if (this.nodes.hostedLoading) this.nodes.hostedLoading.hidden = false;
      if (this.nodes.hostedUnavailable) this.nodes.hostedUnavailable.hidden = true;
      this.dataset.hostedEmbedState = 'loading';
      this.clearHostedEmbedTimer();
      this.hostedEmbedTimer = window.setTimeout(() => this.handleHostedEmbedTimeout(), 15000);
    }

    handleHostedIframeLoad() {
      this.clearHostedEmbedTimer();
      this.dataset.hostedEmbedState = 'loaded';
      if (this.nodes.hostedLoading) this.nodes.hostedLoading.hidden = true;
      if (this.nodes.hostedUnavailable) this.nodes.hostedUnavailable.hidden = true;
    }

    handleHostedEmbedTimeout() {
      this.hostedEmbedTimer = null;
      this.dataset.hostedEmbedState = 'unavailable';
      if (this.nodes.hostedLoading) this.nodes.hostedLoading.hidden = true;
      if (this.nodes.hostedUnavailable) this.nodes.hostedUnavailable.hidden = false;
    }

    clearHostedEmbedTimer() {
      if (this.hostedEmbedTimer) window.clearTimeout(this.hostedEmbedTimer);
      this.hostedEmbedTimer = null;
    }

    initializeCurrentReference() {
      this.dataset.tone = 'neutral';
      this.renderShopifyVariant();
      const cachedQuote = this.readCurrentReferenceCache();
      if (cachedQuote) {
        this.currentReferenceQuote = cachedQuote;
        this.latestQuoteTimestamp = Math.max(this.latestQuoteTimestamp, cachedQuote.providerUpdatedAt);
        this.renderCurrentReferenceQuote(cachedQuote, 'cached');
      } else {
        this.renderCurrentReferenceLoading();
      }

      if (document.hidden) return;
      if (!this.isOnline()) {
        this.renderCurrentReferenceFailure();
        return;
      }
      const cachedAge = cachedQuote ? Date.now() - cachedQuote.fetchedAt : Number.POSITIVE_INFINITY;
      if (cachedAge >= CURRENT_REFERENCE_REFRESH_MS) {
        this.refreshCurrentReference();
      } else {
        this.scheduleCurrentReferenceRefresh(CURRENT_REFERENCE_REFRESH_MS - cachedAge);
      }
    }

    resumeCurrentReference() {
      if (!this.isCurrentReferenceMode() || document.hidden || !this.isOnline() || this.abortController) return;
      const quote = this.currentReferenceQuote;
      const age = quote ? Date.now() - quote.fetchedAt : Number.POSITIVE_INFINITY;
      if (age >= CURRENT_REFERENCE_REFRESH_MS) {
        if (this.isCurrentReferenceStale(quote)) this.renderCurrentReferenceFailure();
        this.refreshCurrentReference();
      } else {
        this.renderCurrentReferenceQuote(quote, 'cached');
        this.scheduleCurrentReferenceRefresh(CURRENT_REFERENCE_REFRESH_MS - age);
      }
    }

    async refreshCurrentReference() {
      if (!this.isCurrentReferenceMode() || document.hidden || !this.isOnline() || this.abortController) return;

      this.clearPollTimer();
      if (!this.currentReferenceQuote) this.renderCurrentReferenceLoading();
      const requestSequence = ++this.requestSequence;
      const controller = new AbortController();
      let timedOut = false;
      this.abortController = controller;
      const timeoutHandle = window.setTimeout(() => {
        timedOut = true;
        if (this.abortController === controller) controller.abort();
      }, CURRENT_REFERENCE_TIMEOUT_MS);
      this.currentReferenceTimeout = timeoutHandle;

      try {
        const response = await fetch(CURRENT_REFERENCE_URL, {
          cache: 'no-store',
          credentials: 'omit',
          headers: { Accept: 'application/json' },
          method: 'GET',
          mode: 'cors',
          redirect: 'error',
          referrerPolicy: 'no-referrer',
          signal: controller.signal,
        });
        const quote = await this.normalizeCurrentReferenceResponse(response);
        if (!this.isCurrentReferenceRequest(requestSequence)) return;

        this.failureCount = 0;
        this.latestQuoteTimestamp = quote.providerUpdatedAt;
        this.currentReferenceQuote = quote;
        this.writeCurrentReferenceCache(quote);
        this.renderCurrentReferenceQuote(quote, 'fresh');
        this.scheduleCurrentReferenceRefresh(CURRENT_REFERENCE_REFRESH_MS);
      } catch (error) {
        if (error && error.name === 'AbortError' && !timedOut) return;
        if (this.isCurrentReferenceRequest(requestSequence)) this.renderCurrentReferenceFailure();
      } finally {
        if (this.currentReferenceTimeout === timeoutHandle) this.clearCurrentReferenceTimeout();
        if (this.isCurrentReferenceRequest(requestSequence)) this.abortController = null;
      }
    }

    isCurrentReferenceRequest(requestSequence) {
      return this.isConnected
        && this.isCurrentReferenceMode()
        && !document.hidden
        && this.isOnline()
        && requestSequence === this.requestSequence;
    }

    async normalizeCurrentReferenceResponse(response) {
      if (!response || !response.ok || response.redirected) throw new Error('Current reference request failed');
      const responseUrl = new URL(response.url || CURRENT_REFERENCE_URL);
      if (responseUrl.href !== CURRENT_REFERENCE_URL) throw new Error('Current reference redirect is invalid');
      if (!(response.headers.get('content-type') || '').toLowerCase().includes('application/json')) {
        throw new Error('Current reference response is not JSON');
      }
      const contentLength = Number(response.headers.get('content-length'));
      if (Number.isFinite(contentLength) && contentLength > CURRENT_REFERENCE_MAX_RESPONSE_BYTES) {
        throw new Error('Current reference response is too large');
      }
      const body = await response.text();
      if (body.length > CURRENT_REFERENCE_MAX_RESPONSE_BYTES) throw new Error('Current reference response is too large');

      let payload;
      try {
        payload = JSON.parse(body);
      } catch (error) {
        throw new Error('Current reference response is malformed');
      }
      return this.normalizeCurrentReferencePayload(payload, Date.now(), true);
    }

    normalizeCurrentReferencePayload(payload, fetchedAt, enforceTimestampOrdering) {
      if (!isPlainObject(payload)) throw new Error('Current reference payload is invalid');
      const symbol = asString(payload.symbol).toUpperCase();
      const currency = asString(payload.currency).toUpperCase();
      const price = asFiniteNumber(payload.price);
      const providerUpdatedAtRaw = asString(payload.updatedAt);
      const providerUpdatedAt = Date.parse(providerUpdatedAtRaw);
      if (symbol !== 'XAU' || currency !== 'USD' || !isFiniteNumber(price) || price <= 0) {
        throw new Error('Current reference instrument is invalid');
      }
      if (!Number.isFinite(providerUpdatedAt) || providerUpdatedAt <= 0 || providerUpdatedAt > Date.now() + MAX_FUTURE_TIMESTAMP_MS) {
        throw new Error('Current reference timestamp is invalid');
      }
      if (!isFiniteNumber(fetchedAt) || fetchedAt <= 0 || fetchedAt > Date.now() + MAX_FUTURE_TIMESTAMP_MS) {
        throw new Error('Current reference cache timestamp is invalid');
      }
      if (enforceTimestampOrdering && providerUpdatedAt < this.latestQuoteTimestamp) {
        throw new Error('Current reference timestamp is older than the verified quote');
      }
      return {
        currency,
        fetchedAt,
        price,
        providerUpdatedAt,
        providerUpdatedAtRaw,
        symbol,
      };
    }

    readCurrentReferenceCache() {
      try {
        const stored = window.localStorage.getItem(CURRENT_REFERENCE_CACHE_KEY);
        if (!stored || stored.length > CURRENT_REFERENCE_MAX_RESPONSE_BYTES) return null;
        const cached = JSON.parse(stored);
        if (!isPlainObject(cached)) return null;
        return this.normalizeCurrentReferencePayload({
          currency: cached.currency,
          price: cached.price,
          symbol: cached.symbol,
          updatedAt: cached.providerUpdatedAt,
        }, cached.fetchedAt, false);
      } catch (error) {
        return null;
      }
    }

    writeCurrentReferenceCache(quote) {
      try {
        window.localStorage.setItem(CURRENT_REFERENCE_CACHE_KEY, JSON.stringify({
          currency: quote.currency,
          fetchedAt: quote.fetchedAt,
          price: quote.price,
          providerUpdatedAt: quote.providerUpdatedAtRaw,
          symbol: quote.symbol,
        }));
      } catch (error) {
        // Storage is optional. A blocked or full local store never blocks the reference.
      }
    }

    isCurrentReferenceStale(quote) {
      return !quote || Date.now() - quote.fetchedAt > CURRENT_REFERENCE_MAX_STALE_MS;
    }

    renderCurrentReferenceLoading() {
      this.dataset.tone = 'neutral';
      this.setState('current_reference', 'LOADING REFERENCE');
      this.setText(this.nodes.goldPrice, '—');
      this.hide(this.nodes.goldChange);
      this.setText(this.nodes.updated, 'Loading market reference…');
      this.renderShopifyVariant();
    }

    renderCurrentReferenceFailure() {
      this.failureCount += 1;
      if (this.currentReferenceQuote && !this.isCurrentReferenceStale(this.currentReferenceQuote)) {
        this.renderCurrentReferenceQuote(this.currentReferenceQuote, 'cached');
      } else if (this.currentReferenceQuote && Date.now() - this.currentReferenceQuote.fetchedAt <= CURRENT_REFERENCE_MAX_STALE_MS) {
        this.renderCurrentReferenceQuote(this.currentReferenceQuote, 'stale');
      } else {
        this.dataset.tone = 'neutral';
        this.setState('error', 'REFERENCE UNAVAILABLE');
        this.setText(this.nodes.goldPrice, '—');
        this.hide(this.nodes.goldChange);
        this.setText(this.nodes.updated, 'Market reference temporarily unavailable.');
        this.renderShopifyVariant();
      }
      this.scheduleCurrentReferenceRefresh(CURRENT_REFERENCE_REFRESH_MS);
    }

    renderCurrentReferenceQuote(quote, presentation) {
      if (!quote || this.isCurrentReferenceStale(quote)) {
        this.renderCurrentReferenceFailure();
        return;
      }
      const age = Date.now() - quote.fetchedAt;
      const isCached = presentation === 'cached' && age <= CURRENT_REFERENCE_REFRESH_MS;
      const isStale = presentation === 'stale' || age > CURRENT_REFERENCE_REFRESH_MS;
      const status = isStale ? 'LAST AVAILABLE REFERENCE' : (isCached ? 'CACHED REFERENCE' : 'INDICATIVE REFERENCE');
      this.dataset.tone = 'neutral';
      this.setState('current_reference', status);
      this.setText(this.nodes.goldPrice, this.formatGoldReference(quote.price));
      this.hide(this.nodes.goldChange);
      this.setText(this.nodes.updated, `${isStale ? 'Last updated' : 'Updated'} ${this.formatProviderTimestamp(quote.providerUpdatedAt)}`);
      this.renderShopifyVariant();
    }

    scheduleCurrentReferenceRefresh(delay) {
      this.clearPollTimer();
      if (!this.isCurrentReferenceMode() || document.hidden || !this.isOnline()) return;
      const safeDelay = Math.max(0, Number(delay) || CURRENT_REFERENCE_REFRESH_MS);
      this.pollTimer = window.setTimeout(() => this.refreshCurrentReference(), safeDelay);
    }

    clearCurrentReferenceTimeout() {
      if (this.currentReferenceTimeout !== null) window.clearTimeout(this.currentReferenceTimeout);
      this.currentReferenceTimeout = null;
    }

    async refresh() {
      if (!this.isPrivateProxyMode() || !this.currentVariant || document.hidden || !this.isOnline()) return;

      const url = this.createQuoteUrl();
      if (!url) {
        this.renderError('ENDPOINT UNAVAILABLE');
        return;
      }

      this.clearPollTimer();
      this.abortRequest();
      if (!this.lastValid) this.setState('loading');
      const requestSequence = ++this.requestSequence;
      const requestVariantId = this.currentVariant.id;
      this.abortController = new AbortController();

      try {
        const response = await fetch(url, {
          cache: 'no-store',
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Quote request failed with ${response.status}`);
        if (!(response.headers.get('content-type') || '').toLowerCase().includes('application/json')) {
          throw new Error('Quote response is not JSON');
        }
        const payload = await response.json();

        if (!this.isCurrentRequest(requestSequence, requestVariantId)) return;

        const normalized = this.normalizePayload(payload, requestVariantId);
        if (normalized.state === 'pending') {
          this.failureCount = 0;
          if (this.lastValid) this.renderRetainedQuote('stale');
          else this.renderPending();
          this.scheduleRefresh(normalized.retryAfterMs);
          return;
        }

        if (normalized.state === 'stale') {
          this.failureCount = 0;
          if (this.lastValid) this.renderRetainedQuote('stale');
          else this.renderStaleUnavailable();
          this.scheduleRefresh(normalized.retryAfterMs);
          return;
        }

        if (normalized.state === 'error') {
          this.handleFetchFailure(normalized.retryAfterMs);
          return;
        }

        if (normalized.state === 'market_closed') {
          this.failureCount = 0;
          this.renderMarketClosed();
          this.scheduleRefresh(normalized.retryAfterMs);
          return;
        }

        this.failureCount = 0;
        if (normalized.updatedAt < this.latestQuoteTimestamp) {
          this.scheduleRefresh(normalized.retryAfterMs);
          return;
        }
        this.latestQuoteTimestamp = normalized.updatedAt;
        this.lastValid = normalized;
        this.renderQuote(normalized);
        this.scheduleRefresh(normalized.retryAfterMs);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        if (this.isCurrentRequest(requestSequence, requestVariantId)) this.handleFetchFailure();
      } finally {
        if (this.isCurrentRequest(requestSequence, requestVariantId)) this.abortController = null;
      }
    }

    isCurrentRequest(requestSequence, variantId) {
      return this.isConnected
        && !document.hidden
        && this.isOnline()
        && requestSequence === this.requestSequence
        && this.currentVariant
        && this.currentVariant.id === variantId;
    }

    createQuoteUrl() {
      try {
        const url = new URL(this.endpoint, window.location.origin);
        if (
          url.origin !== window.location.origin
          || url.pathname !== '/apps/1lux-gold-market/v1/quote'
          || url.username
          || url.password
        ) return null;
        url.search = '';
        url.searchParams.set('variant_id', this.currentVariant.id);
        url.searchParams.set('range', this.range);
        return url;
      } catch (error) {
        return null;
      }
    }

    normalizePayload(payload, requestedVariantId) {
      if (!isPlainObject(payload) || asString(payload.version) !== QUOTE_CONTRACT_VERSION) {
        throw new Error('Quote payload version is invalid');
      }

      const status = asString(payload.status).toLowerCase();
      const retryAfterMs = this.normalizeRetryAfter(payload.retryAfterMs);
      if (status === 'pending' || status === 'error') {
        return {
          state: status,
          retryAfterMs,
          asOf: this.optionalTimestamp(payload.asOf),
        };
      }

      const quote = payload.quote;
      if (status === 'market_closed' && !isPlainObject(quote)) {
        return {
          state: 'market_closed',
          retryAfterMs,
          asOf: this.optionalTimestamp(payload.asOf),
        };
      }
      if (status === 'stale' && !isPlainObject(quote)) {
        return {
          state: 'stale',
          retryAfterMs,
          asOf: this.optionalTimestamp(payload.asOf),
        };
      }
      if ((status !== 'ok' && status !== 'stale' && status !== 'market_closed') || !isPlainObject(quote)) {
        throw new Error('Quote status is invalid');
      }

      const currency = asString(quote.currency).toUpperCase();
      const symbol = asString(quote.symbol).toUpperCase();
      const price = this.requiredNumber(quote.price, 'Quote price', true);
      const asOf = this.requiredTimestamp(quote.asOf, 'Quote timestamp');
      const receivedAt = this.requiredTimestamp(quote.receivedAt, 'Quote receipt timestamp');
      const change = this.optionalNumber(quote.change, 'Quote change');
      const changePct = this.optionalNumber(quote.changePct, 'Quote change percentage');
      const changeBasis = this.normalizeChangeBasis(quote.changeBasis);

      if (currency !== 'USD' || currency !== this.currency.toUpperCase() || symbol !== 'XAUUSD') {
        throw new Error('Quote instrument is invalid');
      }
      if ((change === null) !== (changePct === null)) throw new Error('Quote change is incomplete');
      if (change !== null && !changeBasis) throw new Error('Quote change basis is missing');

      const series = payload.series;
      if (!isPlainObject(series) || !Array.isArray(series.points)) throw new Error('Quote history is invalid');

      const attribution = payload.attribution == null ? null : this.normalizeAttribution(payload.attribution);
      const product = this.normalizeProductReference(payload.product, requestedVariantId);

      return {
        state: 'quote',
        retryAfterMs,
        marketClosed: status === 'market_closed',
        stale: status === 'stale' || status === 'market_closed',
        updatedAt: asOf,
        receivedAt,
        attribution,
        gold: {
          price,
          bid: this.optionalNumber(quote.bid, 'Quote bid', true),
          ask: this.optionalNumber(quote.ask, 'Quote ask', true),
          change,
          changeBasis,
          changePercent: changePct,
          history: this.cleanHistory(series.points, 'price'),
          historyLabel: asString(series.label).slice(0, 80) || 'XAUUSD',
        },
        product,
      };
    }

    normalizeProductReference(product, requestedVariantId) {
      if (product == null) return null;
      if (!isPlainObject(product)) throw new Error('Product reference is invalid');

      const variantId = asString(product.variantId);
      if (!isSameVariant(variantId, requestedVariantId)) throw new Error('Product reference does not match the selection');

      const marketReferenceCents = this.optionalNumber(product.marketReferenceCents, 'Product market reference', true);
      const changeCents = this.optionalNumber(product.changeCents, 'Product change');
      const changePct = this.optionalNumber(product.changePct, 'Product change percentage');
      if ((changeCents === null) !== (changePct === null)) throw new Error('Product change is incomplete');

      const productHistory = product.history == null ? [] : this.cleanHistory(product.history, 'priceCents');
      if (marketReferenceCents === null && (changeCents !== null || productHistory.length > 0)) {
        throw new Error('Product reference is incomplete');
      }

      return {
        asOf: this.optionalTimestamp(product.asOf),
        changeCents,
        changePercent: changePct,
        history: productHistory,
        marketReferenceCents,
      };
    }

    normalizeAttribution(attribution) {
      if (!isPlainObject(attribution)) throw new Error('Quote attribution is invalid');
      const label = asString(attribution.label);
      if (!label || label.length > 120) throw new Error('Quote attribution label is invalid');
      return label;
    }

    normalizeChangeBasis(value) {
      if (value == null) return null;
      const basis = asString(value);
      return Object.prototype.hasOwnProperty.call(CHANGE_BASIS_LABELS, basis) ? basis : null;
    }

    requiredNumber(value, label, mustBePositive) {
      const number = asFiniteNumber(value);
      if (!isFiniteNumber(number) || (mustBePositive && number <= 0)) throw new Error(`${label} is invalid`);
      return number;
    }

    optionalNumber(value, label, mustBePositive) {
      if (value == null) return null;
      return this.requiredNumber(value, label, mustBePositive);
    }

    requiredTimestamp(value, label) {
      const timestamp = Date.parse(asString(value));
      if (!Number.isFinite(timestamp) || timestamp <= 0 || timestamp > Date.now() + MAX_FUTURE_TIMESTAMP_MS) {
        throw new Error(`${label} is invalid`);
      }
      return timestamp;
    }

    optionalTimestamp(value) {
      return value == null ? null : this.requiredTimestamp(value, 'Quote timestamp');
    }

    normalizeRetryAfter(value) {
      const retryAfterMs = asFiniteNumber(value);
      if (!isFiniteNumber(retryAfterMs)) return this.refreshMilliseconds;
      return Math.max(MIN_RETRY_MS, Math.min(MAX_RETRY_MS, Math.round(retryAfterMs)));
    }

    cleanHistory(history, valueKey) {
      if (!Array.isArray(history)) return [];
      const validPoints = history.map((point) => ({
        timestamp: Date.parse(asString(point && point.timestamp)),
        value: asFiniteNumber(point && point[valueKey]),
      })).filter((point) => {
        return Number.isFinite(point.timestamp)
          && point.timestamp > 0
          && point.timestamp <= Date.now() + MAX_FUTURE_TIMESTAMP_MS
          && isFiniteNumber(point.value)
          && point.value > 0;
      }).sort((first, second) => first.timestamp - second.timestamp);

      const uniquePoints = [];
      validPoints.forEach((point) => {
        const previous = uniquePoints[uniquePoints.length - 1];
        if (previous && previous.timestamp === point.timestamp) uniquePoints[uniquePoints.length - 1] = point;
        else uniquePoints.push(point);
      });
      return uniquePoints.slice(-MAX_PUBLIC_HISTORY_POINTS);
    }

    handleFetchFailure(retryAfterMs) {
      this.failureCount += 1;
      this.renderError('FEED UNAVAILABLE');
      const delay = Math.min(this.normalizeRetryAfter(retryAfterMs) * (2 ** Math.min(this.failureCount, 4)), MAX_RETRY_MS);
      this.scheduleRefresh(delay);
    }

    scheduleRefresh(delay) {
      this.clearPollTimer();
      if (!this.isPrivateProxyMode() || document.hidden || !this.isOnline()) return;
      const boundedDelay = Math.max(MIN_RETRY_MS, Math.min(MAX_RETRY_MS, Number(delay) || this.refreshMilliseconds));
      const jitter = Math.round(boundedDelay * ((Math.random() * 0.2) - 0.1));
      this.pollTimer = window.setTimeout(() => this.refresh(), Math.max(MIN_RETRY_MS, boundedDelay + jitter));
    }

    clearPollTimer() {
      if (this.pollTimer) window.clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }

    abortRequest() {
      if (this.abortController) this.abortController.abort();
      this.abortController = null;
      this.clearCurrentReferenceTimeout();
    }

    startAgeTimer() {
      if (this.ageInterval) window.clearInterval(this.ageInterval);
      this.ageInterval = window.setInterval(() => this.updateAge(), 1000);
    }

    updateAge() {
      if (!this.lastValid) return;

      const age = Date.now() - this.lastValid.updatedAt;
      if (this.nodes.updated) this.nodes.updated.textContent = this.formatUpdated(this.lastValid, age);

      if (this.dataset.state === 'live' || this.dataset.state === 'stale') {
        const isStale = this.lastValid.stale || age >= this.staleThresholdMilliseconds;
        this.dataset.tone = isStale ? 'neutral' : this.toneFor(this.lastValid.gold.change);
        if (this.nodes.productCard) {
          const productChange = this.lastValid.product ? this.lastValid.product.changeCents : null;
          this.nodes.productCard.dataset.tone = isStale ? 'neutral' : this.toneFor(productChange);
        }
        this.setState(isStale ? 'stale' : 'live');
      }
    }

    renderShopifyVariant() {
      if (!this.currentVariant) {
        this.renderUnavailableVariant();
        return;
      }

      this.setText(this.nodes.productTitle, this.productTitle);
      this.setText(this.nodes.productVariant, this.formatVariantTitle(this.currentVariant.title));
      this.setText(this.nodes.productPrimaryPrice, this.formatCents(this.currentVariant.checkoutPriceCents));
      this.setText(this.nodes.productPrimaryLabel, 'Shopify checkout price');
      if (this.nodes.checkoutComparison) this.nodes.checkoutComparison.hidden = true;
      if (this.nodes.trackButton && this.trackingEnabled) this.nodes.trackButton.disabled = false;
      this.hide(this.nodes.productChange);
      this.clearChart(this.nodes.productArea, this.nodes.productLine, this.nodes.productChart, 'Selected product market data pending');
      if (this.nodes.productCard) this.nodes.productCard.dataset.tone = 'neutral';
      this.renderTicker();
    }

    renderUnavailableVariant() {
      this.setText(this.nodes.productTitle, this.productTitle);
      this.setText(this.nodes.productVariant, 'Select an available option');
      this.setText(this.nodes.productPrimaryPrice, '—');
      this.setText(this.nodes.productPrimaryLabel, 'Shopify checkout price');
      if (this.nodes.checkoutComparison) this.nodes.checkoutComparison.hidden = true;
      if (this.nodes.trackButton) this.nodes.trackButton.disabled = true;
      this.hide(this.nodes.productChange);
      this.clearChart(this.nodes.productArea, this.nodes.productLine, this.nodes.productChart, 'Selected product configuration is unavailable');
      if (this.nodes.productCard) this.nodes.productCard.dataset.tone = 'neutral';
      this.renderTicker();
    }

    renderPending() {
      this.lastValid = null;
      this.setState('pending');
      this.dataset.tone = 'neutral';
      this.setText(this.nodes.goldPrice, '—');
      this.hide(this.nodes.goldChange);
      if (this.nodes.updated) this.nodes.updated.textContent = 'FEED PENDING';
      this.renderAttribution(null);
      this.clearChart(this.nodes.marketArea, this.nodes.marketLine, this.nodes.marketChart, 'XAUUSD market data pending');
      this.renderShopifyVariant();
    }

    renderError(label) {
      this.setState('error', label);
      this.dataset.tone = 'neutral';

      if (!this.lastValid) {
        this.setText(this.nodes.goldPrice, '—');
        this.hide(this.nodes.goldChange);
        this.clearChart(this.nodes.marketArea, this.nodes.marketLine, this.nodes.marketChart, 'XAUUSD market data unavailable');
        this.renderShopifyVariant();
        if (this.nodes.updated) this.nodes.updated.textContent = 'FEED UNAVAILABLE';
        this.renderAttribution(null);
        return;
      }

      // The checkout price is always taken from the selected Shopify variant,
      // never from a retained market response.
      this.renderShopifyVariant();
      if (this.nodes.productCard) this.nodes.productCard.dataset.tone = 'neutral';
      this.renderTicker();
      if (this.nodes.updated) this.nodes.updated.textContent = this.formatUpdated(this.lastValid);
    }

    renderRetainedQuote(state) {
      if (!this.lastValid) {
        this.renderStaleUnavailable();
        return;
      }

      this.renderQuote({ ...this.lastValid, stale: true });
      this.dataset.tone = 'neutral';
      if (this.nodes.productCard) this.nodes.productCard.dataset.tone = 'neutral';
      this.setState(state);
    }

    renderStaleUnavailable() {
      this.setState('stale');
      this.dataset.tone = 'neutral';
      this.setText(this.nodes.goldPrice, '—');
      this.hide(this.nodes.goldChange);
      this.clearChart(this.nodes.marketArea, this.nodes.marketLine, this.nodes.marketChart, 'No verified XAUUSD quote is available');
      this.renderShopifyVariant();
      if (this.nodes.updated) this.nodes.updated.textContent = 'STALE — NO VERIFIED QUOTE';
      this.renderAttribution(null);
    }

    renderMarketClosed() {
      if (this.lastValid) {
        this.renderRetainedQuote('market_closed');
        return;
      }

      this.setState('market_closed');
      this.dataset.tone = 'neutral';
      this.setText(this.nodes.goldPrice, '—');
      this.hide(this.nodes.goldChange);
      this.clearChart(this.nodes.marketArea, this.nodes.marketLine, this.nodes.marketChart, 'XAUUSD market is closed');
      this.renderShopifyVariant();
      if (this.nodes.updated) this.nodes.updated.textContent = 'MARKET CLOSED';
      this.renderAttribution(null);
    }

    renderQuote(quote) {
      const isStale = quote.marketClosed || quote.stale || Date.now() - quote.updatedAt >= this.staleThresholdMilliseconds;
      const marketTone = isStale ? 'neutral' : this.toneFor(quote.gold.change);
      const productTone = isStale || !quote.product ? 'neutral' : this.toneFor(quote.product.changeCents);
      this.dataset.tone = marketTone;
      this.setState(quote.marketClosed ? 'market_closed' : (isStale ? 'stale' : 'live'));

      this.animatePrice(this.nodes.goldPrice, 'gold', quote.gold.price, (value) => this.formatGold(value));
      this.renderChange(
        this.nodes.goldChange,
        quote.gold.change,
        quote.gold.changePercent,
        (value) => this.formatGoldChange(value),
        quote.gold.changeBasis,
      );
      this.drawChart(this.nodes.marketArea, this.nodes.marketLine, this.nodes.marketChart, quote.gold.history, 320, 58, quote.gold.historyLabel);

      this.renderShopifyVariant();
      const product = quote.product;
      const checkoutPriceCents = this.currentVariant ? this.currentVariant.checkoutPriceCents : null;
      if (product && isFiniteNumber(product.marketReferenceCents)) {
        this.animatePrice(this.nodes.productPrimaryPrice, 'product', product.marketReferenceCents, (value) => this.formatCents(value));
        this.setText(this.nodes.productPrimaryLabel, 'Market-linked modeled retail price');

        if (this.nodes.checkoutComparison) {
          const showComparison = this.showCheckoutComparison && product.marketReferenceCents !== checkoutPriceCents;
          this.nodes.checkoutComparison.hidden = !showComparison;
          if (showComparison) this.setText(this.nodes.checkoutComparison, `Shopify checkout price: ${this.formatCents(checkoutPriceCents)}`);
        }

        this.renderChange(this.nodes.productChange, product.changeCents, product.changePercent, (value) => this.formatCentsChange(value));
        if (this.showProductSparkline) {
          this.drawChart(this.nodes.productArea, this.nodes.productLine, this.nodes.productChart, product.history, 320, 38, 'Selected product market reference');
        }
      }
      if (this.nodes.productCard) this.nodes.productCard.dataset.tone = productTone;
      this.renderTicker(quote);
      if (this.nodes.updated) this.nodes.updated.textContent = this.formatUpdated(quote);
      this.renderAttribution(quote.attribution);
    }

    setState(state, customLabel) {
      this.dataset.state = state;
      if (!this.nodes.statusText) return;

      const labels = {
        current_reference: 'INDICATIVE REFERENCE',
        error: 'FEED UNAVAILABLE',
        hosted: 'THIRD-PARTY REFERENCE',
        live: 'LIVE',
        loading: 'UPDATING',
        market_closed: 'MARKET CLOSED',
        pending: 'FEED PENDING',
        stale: 'STALE',
      };
      this.setText(this.nodes.statusText, customLabel || labels[state] || 'FEED PENDING');
    }

    renderChange(node, value, percent, formatter, changeBasis) {
      if (!node) return;
      if (!isFiniteNumber(value) || !isFiniteNumber(percent)) {
        this.hide(node);
        node.removeAttribute('aria-label');
        node.removeAttribute('title');
        return;
      }

      node.hidden = false;
      this.setText(node, `${formatter(value)} · ${this.formatPercent(percent)}`);
      const basisLabel = this.formatChangeBasis(changeBasis);
      node.setAttribute('aria-label', basisLabel ? `${node.textContent}, ${basisLabel}` : node.textContent);
      if (basisLabel) node.setAttribute('title', basisLabel);
      else node.removeAttribute('title');
    }

    formatChangeBasis(changeBasis) {
      return CHANGE_BASIS_LABELS[changeBasis] || '';
    }

    renderAttribution(label) {
      if (!this.nodes.attribution) return;
      const attribution = asString(label);
      this.nodes.attribution.hidden = !attribution;
      this.setText(this.nodes.attribution, attribution ? ` · ${attribution}` : '');
    }

    renderTicker(quote) {
      if (!this.showProductTicker || !this.nodes.ticker) return;

      if (!this.currentVariant) {
        const unavailableText = [this.productTitle, 'SELECT AN AVAILABLE OPTION'].filter(Boolean).join(' · ');
        this.setText(this.nodes.ticker, unavailableText);
        this.setText(this.nodes.tickerClone, unavailableText);
        return;
      }

      const checkoutPriceCents = this.currentVariant.checkoutPriceCents;
      const parts = [this.productTitle, this.formatVariantTitle(this.currentVariant.title), `Checkout ${this.formatCents(checkoutPriceCents)}`];
      if (this.isHostedEmbedMode()) {
        parts.push('Gold market reference hosted by goldprice.dev');
      } else if (quote) {
        if (quote.product && isFiniteNumber(quote.product.marketReferenceCents)) {
          parts.push(`Market-linked ${this.formatCents(quote.product.marketReferenceCents)}`);
          if (isFiniteNumber(quote.product.changeCents) && isFiniteNumber(quote.product.changePercent)) {
            parts.push(`${this.formatCentsChange(quote.product.changeCents)} · ${this.formatPercent(quote.product.changePercent)}`);
          }
        }
        parts.push(`XAUUSD ${this.formatGold(quote.gold.price)}`);
        if (isFiniteNumber(quote.gold.change) && isFiniteNumber(quote.gold.changePercent)) {
          parts.push(`${this.formatGoldChange(quote.gold.change)} · ${this.formatPercent(quote.gold.changePercent)}`);
          const basisLabel = this.formatChangeBasis(quote.gold.changeBasis);
          if (basisLabel) parts.push(basisLabel);
        }
      } else {
        const stateLabel = {
          error: 'FEED UNAVAILABLE',
          market_closed: 'MARKET CLOSED',
          stale: 'STALE',
        };
        parts.push(stateLabel[this.dataset.state] || 'FEED PENDING');
      }

      const text = parts.filter(Boolean).join(' · ');
      this.setText(this.nodes.ticker, text);
      this.setText(this.nodes.tickerClone, text);
    }

    drawChart(areaNode, lineNode, chartNode, history, width, height, label) {
      if (!areaNode || !lineNode || !chartNode) return;
      if (!Array.isArray(history) || history.length < 2) {
        this.clearChart(areaNode, lineNode, chartNode, `${label} has no live history`);
        return;
      }

      const values = history.map((point) => point.value).filter(isFiniteNumber);
      if (values.length < 2) {
        this.clearChart(areaNode, lineNode, chartNode, `${label} has no live history`);
        return;
      }

      const render = () => {
        const padding = 4;
        const minimum = Math.min(...values);
        const maximum = Math.max(...values);
        const range = maximum - minimum || 1;
        if (!Number.isFinite(minimum) || !Number.isFinite(maximum) || !Number.isFinite(range)) {
          this.clearChart(areaNode, lineNode, chartNode, `${label} has no valid history`);
          return;
        }
        const points = values.map((value, index) => {
          const x = (index / (values.length - 1)) * width;
          const y = height - padding - ((value - minimum) / range) * (height - padding * 2);
          return [x, y];
        });
        if (points.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) {
          this.clearChart(areaNode, lineNode, chartNode, `${label} has no valid history`);
          return;
        }
        const line = points.map(([x, y], index) => `${index === 0 ? 'M' : 'L'}${x.toFixed(2)} ${y.toFixed(2)}`).join(' ');
        const area = `M0 ${height - padding} ${line} L${width} ${height - padding} Z`;
        const movement = values[values.length - 1] - values[0];
        const direction = movement > 0 ? 'rising' : movement < 0 ? 'falling' : 'flat';

        lineNode.setAttribute('d', line);
        areaNode.setAttribute('d', area);
        chartNode.setAttribute('aria-label', `${label} history is ${direction} over the selected period`);
        const stage = chartNode.closest('.one-lux-market__chart-stage');
        if (stage) stage.hidden = false;
      };

      if (this.reducedMotion) {
        render();
      } else {
        window.requestAnimationFrame(render);
      }
    }

    clearChart(areaNode, lineNode, chartNode, label) {
      if (areaNode) areaNode.removeAttribute('d');
      if (lineNode) lineNode.removeAttribute('d');
      if (chartNode) {
        chartNode.setAttribute('aria-label', label);
        const stage = chartNode.closest('.one-lux-market__chart-stage');
        if (stage) stage.hidden = true;
      }
    }

    animatePrice(node, key, nextValue, formatter) {
      if (!node || !isFiniteNumber(nextValue)) return;
      const renderedValue = formatter(nextValue);
      // Only verified provider values are shown. The text is never numerically tweened.
      this.setText(node, renderedValue);
    }

    setText(node, value) {
      if (node && node.textContent !== value) node.textContent = value;
    }

    hide(node) {
      if (node) node.hidden = true;
    }

    toneFor(value) {
      if (!isFiniteNumber(value) || value === 0) return 'neutral';
      return value > 0 ? 'up' : 'down';
    }

    formatVariantTitle(title) {
      return asString(title) === 'Default Title' ? 'Selected configuration' : asString(title);
    }

    formatGold(value) {
      return new Intl.NumberFormat('en-US', {
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
      }).format(value);
    }

    formatGoldReference(value) {
      return new Intl.NumberFormat('en-US', {
        currency: 'USD',
        maximumFractionDigits: 2,
        minimumFractionDigits: 2,
        style: 'currency',
      }).format(value);
    }

    formatProviderTimestamp(timestamp) {
      return `${new Intl.DateTimeFormat('en-US', {
        dateStyle: 'medium',
        timeStyle: 'short',
        timeZone: 'UTC',
      }).format(new Date(timestamp))} UTC`;
    }

    formatGoldChange(value) {
      const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
      return `${prefix}${this.formatGold(Math.abs(value))}`;
    }

    formatCents(value) {
      return new Intl.NumberFormat(undefined, {
        currency: this.currency,
        currencyDisplay: 'narrowSymbol',
        style: 'currency',
      }).format(value / 100);
    }

    formatCentsChange(value) {
      const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
      return `${prefix}${this.formatCents(Math.abs(value))}`;
    }

    formatPercent(value) {
      const prefix = value > 0 ? '+' : value < 0 ? '-' : '';
      return `${prefix}${Math.abs(value).toFixed(2)}%`;
    }

    formatAge(ageMilliseconds) {
      const seconds = Math.max(0, Math.floor(ageMilliseconds / 1000));
      const minutes = Math.floor(seconds / 60);
      const remainder = String(seconds % 60).padStart(2, '0');
      return `Updated ${minutes}:${remainder} ago`;
    }

    formatUpdated(quote, ageMilliseconds) {
      const age = isFiniteNumber(ageMilliseconds) ? ageMilliseconds : Date.now() - quote.updatedAt;
      return this.formatAge(age);
    }

    async handleTracking() {
      if (!this.trackingEnabled || !this.currentVariant || !this.nodes.trackButton) return;

      const endpoint = this.createTrackingUrl();
      if (!endpoint) return;

      const wasTracked = this.nodes.trackButton.getAttribute('aria-pressed') === 'true';
      const action = wasTracked ? 'untrack' : 'track';
      this.nodes.trackButton.disabled = true;
      if (this.nodes.trackMessage) this.nodes.trackMessage.textContent = '';

      try {
        const response = await fetch(endpoint, {
          body: JSON.stringify({ action, variant_id: this.currentVariant.id }),
          credentials: 'same-origin',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          method: 'POST',
        });

        if (response.status === 401 || response.redirected) {
          this.redirectToLogin();
          return;
        }
        if (!response.ok) throw new Error('Tracking request failed');
        if (!(response.headers.get('content-type') || '').toLowerCase().includes('application/json')) {
          throw new Error('Tracking endpoint returned a non-JSON response');
        }

        const result = await response.json();
        if (typeof result.tracked !== 'boolean') throw new Error('Tracking response is incomplete');
        const tracked = result.tracked;
        this.nodes.trackButton.setAttribute('aria-pressed', String(tracked));
        if (this.nodes.trackLabel) this.nodes.trackLabel.textContent = tracked ? 'Untrack This Chain' : 'Track This Chain';
        if (this.nodes.trackMessage) {
          this.nodes.trackMessage.textContent = tracked
            ? 'This chain is now tracked.'
            : 'This chain is no longer tracked.';
        }
      } catch (error) {
        if (this.nodes.trackMessage) this.nodes.trackMessage.textContent = 'Tracking is temporarily unavailable.';
      } finally {
        this.nodes.trackButton.disabled = false;
      }
    }

    createTrackingUrl() {
      try {
        const url = new URL(this.trackingEndpoint, window.location.origin);
        return url.origin === window.location.origin && !url.username && !url.password ? url : null;
      } catch (error) {
        return null;
      }
    }

    redirectToLogin() {
      const returnUrl = new URL(window.location.href);
      returnUrl.searchParams.set('variant', this.currentVariant.id);
      const loginUrl = new URL(this.loginUrl, window.location.origin);
      loginUrl.searchParams.set('return_url', `${returnUrl.pathname}${returnUrl.search}${returnUrl.hash}`);
      window.location.assign(loginUrl.toString());
    }
  }

  window.customElements.define(COMPONENT_NAME, OneLuxGoldMarket);
})();
